'use client'

import { useState } from 'react'

interface Props {
  product: 'teleportlink' | 'rowlab' | 'memorylinks' | 'atlas'
  accentClass?: 'cyan' | 'emerald' | 'blue' | 'violet'
  placeholderMessage?: string
  submitLabel?: string
  successTitle?: string
  successBody?: string
}

export default function ProductInterestForm({
  product,
  accentClass = 'cyan',
  placeholderMessage,
  submitLabel = 'Notify me at launch',
  successTitle = "You're on the list.",
  successBody = "We'll reach out when it's ready.",
}: Props) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState('')

  const accent = accentClass === 'emerald'
    ? {
        ring: 'focus:ring-emerald-500/40 focus:border-emerald-500/30',
        btn: 'bg-emerald-500/50 border-emerald-500/30 hover:bg-emerald-500/70',
        badge: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10',
        title: 'text-emerald-300',
      }
    : accentClass === 'blue'
    ? {
        ring: 'focus:ring-blue-500/40 focus:border-blue-500/30',
        btn: 'bg-blue-500/60 border-blue-500/30 hover:bg-blue-500/75',
        badge: 'text-blue-400 border-blue-500/30 bg-blue-500/10',
        title: 'text-blue-300',
      }
    : accentClass === 'violet'
    ? {
        ring: 'focus:ring-violet-500/40 focus:border-violet-500/30',
        btn: 'bg-violet-500/50 border-violet-500/30 hover:bg-violet-500/70',
        badge: 'text-violet-400 border-violet-500/30 bg-violet-500/10',
        title: 'text-violet-300',
      }
    : {
        ring: 'focus:ring-cyan-500/40 focus:border-cyan-500/30',
        btn: 'bg-cyan-500/50 border-cyan-500/30 hover:bg-cyan-500/70',
        badge: 'text-cyan-400 border-cyan-500/30 bg-cyan-500/10',
        title: 'text-cyan-300',
      }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (website) return // honeypot triggered

    if (!email.trim()) {
      setError('Email is required')
      return
    }

    setSubmitting(true)
    try {
      const res = await fetch('/api/product-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product,
          name: name.trim() || undefined,
          email: email.trim(),
          message: message.trim() || undefined,
          website,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit')
      setDone(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className={`p-6 rounded-[20px] border ${accent.badge} text-center`}>
        <p className={`text-lg font-light ${accent.title}`}>{successTitle}</p>
        <p className="text-sm text-gray-400 mt-1">{successBody}</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot — hidden */}
      <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your name (optional)"
          className={`w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${accent.ring} transition-all text-sm`}
        />
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email address *"
          required
          className={`w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${accent.ring} transition-all text-sm`}
        />
      </div>

      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        placeholder={placeholderMessage ?? 'What would you use it for? (optional)'}
        className={`w-full px-4 py-3 rounded-[12px] border border-white/10 bg-white/5 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${accent.ring} transition-all text-sm resize-none`}
      />

      {error && <p className="text-sm text-red-400">{error}</p>}

      <button
        type="submit"
        disabled={submitting}
        className={`px-8 py-3.5 border text-white rounded-[14px] font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${accent.btn}`}
      >
        {submitting ? 'Submitting…' : submitLabel}
      </button>
    </form>
  )
}
